import React, { useEffect, useMemo, useState } from "react";
import {
  companySiteLocationListApi,
  staffAddressListApi,
} from "../../../../helpers/fakebackend_helper";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDropDown,
  CalendarMonth,
  ContentCopy,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
  LocationOn,
} from "@mui/icons-material";

import {
  createStaffWorkingSchedule,
  updateStaffWorkingSchedule,
  searchStaffWorkingSchedule,
  searchStaffWorkingScheduleForClone,
  getStaffList,
} from "../../../../store/staff/action";
import { toast } from "react-toastify";
import filterActiveStaff from "../../../../Components/Common/staffActive";
import ScheduleAdjustments from "./ScheduleAdjustments";
import ShiftLocationPicker from "./ShiftLocationPicker";
import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";

const DAYS = [
  { key: "mon", label: "Monday", flag: "FlagMon" },
  { key: "tue", label: "Tuesday", flag: "FlagTue" },
  { key: "wed", label: "Wednesday", flag: "FlagWed" },
  { key: "thu", label: "Thursday", flag: "FlagThu" },
  { key: "fri", label: "Friday", flag: "FlagFri" },
  { key: "sat", label: "Saturday", flag: "FlagSat" },
  { key: "sun", label: "Sunday", flag: "FlagSun" },
];

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri"];
const WEEKEND_KEYS = ["sat", "sun"];
const EVERYDAY_KEYS = DAYS.map((day) => day.key);

const DEFAULT_SHIFT = {
  id: null,
  startTime: "09:00",
  endTime: "17:00",
  breakMins: 30,
  // Flexible-availability fields (only used when the schedule is Flexible).
  // dailyCapMins/minimumMins are stored in MINUTES to match the API.
  dailyCapMins: null,
  minimumMins: null,
  capRule: "Span", // 'Span' (first-to-last) | 'Worked' (summed durations)
  notes: "",
  // Start-of-shift location (e.g. the office). Also used by the diary engine
  // as the day's starting point for travel ranking.
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  addressLine4: "",
  addressCity: "",
  addressCounty: "",
  addressPostCode: "",
  addressCountry: "",
  // End-of-shift location (e.g. home). Used as the "drive-home" destination
  // for the last job of the day in detour ranking.
  endAddressLine1: "",
  endAddressLine2: "",
  endAddressLine3: "",
  endAddressLine4: "",
  endAddressCity: "",
  endAddressCounty: "",
  endAddressPostCode: "",
  endAddressCountry: "",
};

const DEFAULT_DAY = {
  working: false,
  shifts: [{ ...DEFAULT_SHIFT }],
};

const buildDefaultDays = () =>
  DAYS.reduce((acc, day) => {
    acc[day.key] = {
      working: false,
      shifts: [{ ...DEFAULT_SHIFT }],
    };
    return acc;
  }, {});

const truthyFlag = (value) => value === "1" || value === 1 || value === true;

const toApiTime = (value) => {
  if (!value) return "00:00:00";
  return value.length === 5 ? `${value}:00` : value;
};

const fromApiTime = (value) => {
  if (!value) return "09:00";
  return String(value).slice(0, 5);
};

// "HH:mm" -> minutes since midnight
const parseTimeToMinutes = (value) => {
  if (!value) return 0;
  const [h, m] = String(value)
    .split(":")
    .map((n) => Number(n) || 0);
  return h * 60 + m;
};

// minutes -> "Hh MMm" (e.g. 150 -> "2h 30m")
const formatMinutes = (mins) => {
  const total = Math.max(0, Math.round(mins));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

// A shift's end must be after its start. Only checked when BOTH are filled —
// an empty start or end skips validation. No overnight: end must be later the
// same day (you can't start at 07:00 and finish at 04:00).
const isShiftTimeInvalid = (shift) => {
  if (!shift?.startTime || !shift?.endTime) return false;
  return parseTimeToMinutes(shift.endTime) <= parseTimeToMinutes(shift.startTime);
};

const getTodayDateTimeStart = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} 00:00:00`;
};

// "2026-09-29 00:00:00" -> "2026-09-29" for a date input (Change #100164).
// Anything that is not a date to begin with ("NULL", the zero date) is empty.
const toDateInput = (value) => {
  const date = String(value || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && date !== "0000-00-00" ? date : "";
};

const getScheduleIdFromResponse = (response) => {
  if (!response) return null;

  if (Array.isArray(response)) {
    return (
      response?.[0]?.StaffWorkingSchedule_Id ||
      response?.[0]?.staffworkingschedule_id ||
      response?.[0]?.Id ||
      null
    );
  }

  return (
    response.StaffWorkingSchedule_Id ||
    response.staffworkingschedule_id ||
    response.Id ||
    null
  );
};

const getScheduleListFromSearchResponse = (response) => {
  if (!response) return [];

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.staffworkingschedule_list)) {
    return response.staffworkingschedule_list;
  }

  if (Array.isArray(response.data?.staffworkingschedule_list)) {
    return response.data.staffworkingschedule_list;
  }

  return [];
};

const getNewestSchedule = (list) => {
  if (!Array.isArray(list) || !list.length) return null;

  return [...list]
    .filter((item) => truthyFlag(item.FlagActive ?? "1"))
    .sort((a, b) => {
      const aDate = new Date(
        a.UpdatedDateTime || a.CreatedDateTime || a.EffectiveStartDateTime || 0
      ).getTime();

      const bDate = new Date(
        b.UpdatedDateTime || b.CreatedDateTime || b.EffectiveStartDateTime || 0
      ).getTime();

      if (bDate !== aDate) return bDate - aDate;

      return Number(b.Id || 0) - Number(a.Id || 0);
    })[0];
};

const cloneDay = (dayConfig) => ({
  working: Boolean(dayConfig?.working),
  shifts: (dayConfig?.shifts || [{ ...DEFAULT_SHIFT }]).map((shift) => ({
    ...shift,
  })),
});

const scheduleRecordsToDays = (records = []) => {
  const nextDays = buildDefaultDays();

  DAYS.forEach((day) => {
    nextDays[day.key] = {
      working: false,
      shifts: [],
    };
  });

  records
    .filter((record) => truthyFlag(record.FlagActive ?? "1"))
    .forEach((record) => {
      DAYS.forEach((day) => {
        if (!truthyFlag(record[day.flag])) return;

        nextDays[day.key].working = true;
        nextDays[day.key].shifts.push({
          id: record.Id ? Number(record.Id) : null,
          startTime: fromApiTime(record.StartTime),
          endTime: fromApiTime(record.EndTime),
          breakMins: Number(record.BreakTimeAllowanceMins || 30),
          dailyCapMins:
            record.DailyCapMins === null ||
            record.DailyCapMins === undefined ||
            record.DailyCapMins === ""
              ? null
              : Number(record.DailyCapMins),
          minimumMins:
            record.MinimumMins === null ||
            record.MinimumMins === undefined ||
            record.MinimumMins === ""
              ? null
              : Number(record.MinimumMins),
          capRule: record.CapRule || "Span",
          notes: record.Notes || "Usual",
          addressLine1: record.AddressLine1 || "",
          addressLine2: record.AddressLine2 || "",
          addressLine3: record.AddressLine3 || "",
          addressLine4: record.AddressLine4 || "",
          addressCity: record.AddressCity || "",
          addressCounty: record.AddressCounty || "",
          addressPostCode: record.AddressPostCode || "",
          addressCountry: record.AddressCountry || "",
          endAddressLine1: record.EndAddressLine1 || "",
          endAddressLine2: record.EndAddressLine2 || "",
          endAddressLine3: record.EndAddressLine3 || "",
          endAddressLine4: record.EndAddressLine4 || "",
          endAddressCity: record.EndAddressCity || "",
          endAddressCounty: record.EndAddressCounty || "",
          endAddressPostCode: record.EndAddressPostCode || "",
          endAddressCountry: record.EndAddressCountry || "",
        });
      });
    });

  DAYS.forEach((day) => {
    if (!nextDays[day.key].shifts.length) {
      nextDays[day.key].shifts = [{ ...DEFAULT_SHIFT }];
    }
  });

  return nextDays;
};

// Change #100149 - "cant edit or name this". SettingCard used to be declared
// INSIDE StaffWorkingSchedule, so every render made a new component type and
// React remounted everything in it. Typing one letter into Schedule Name (or
// the effective date boxes) re-rendered, remounted the input and dropped focus,
// so the field looked uneditable. At module scope its identity is stable.
const SettingCard = ({
  label,
  title,
  helper,
  children,
  tone = "default",
}) => {
  const tones = {
    default: {
      bgcolor: "rgba(15, 23, 42, 0.02)",
      borderColor: "rgba(15, 23, 42, 0.10)",
    },
    blue: {
      bgcolor: "rgba(25, 118, 210, 0.04)",
      borderColor: "rgba(25, 118, 210, 0.22)",
    },
    amber: {
      bgcolor: "rgba(255, 152, 0, 0.04)",
      borderColor: "rgba(255, 152, 0, 0.28)",
    },
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: "100%",
        minHeight: 74,
        bgcolor: tones[tone]?.bgcolor || tones.default.bgcolor,
        borderColor: tones[tone]?.borderColor || tones.default.borderColor,
      }}
    >
      <Stack spacing={0.75}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>

          {title && (
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
          )}

          {helper && (
            <Typography variant="caption" color="text.secondary" display="block">
              {helper}
            </Typography>
          )}
        </Box>

        {children}
      </Stack>
    </Paper>
  );
};

const StaffWorkingSchedule = ({ staffId: staffIdProp, onSaveSuccess } = {}) => {
  const dispatch = useDispatch();
  const params = useParams();

  const staffId = staffIdProp ?? params.id;
  const isEmbedded = Boolean(staffIdProp);
  const isSavePendingRef = React.useRef(false);
  const [isForever, setIsForever] = useState(true);
  const [effectiveEndDate, setEffectiveEndDate] = useState("");
  const {
    loadingCreateStaffWorkingSchedule,
    createStaffWorkingScheduleSuccess,
    loadingUpdateStaffWorkingSchedule,
    updateStaffWorkingScheduleSuccess,
    loadingSearchStaffWorkingSchedule,
    searchStaffWorkingScheduleSuccess,
    staffListAPI,
    loadingSearchStaffWorkingScheduleForClone,
    searchStaffWorkingScheduleForCloneSuccess,
  } = useSelector((state) => ({
    loadingCreateStaffWorkingSchedule:
      state.Staff?.loadingCreateStaffWorkingSchedule,
    createStaffWorkingScheduleSuccess:
      state.Staff?.createStaffWorkingScheduleSuccess,
    loadingUpdateStaffWorkingSchedule:
      state.Staff?.loadingUpdateStaffWorkingSchedule,
    updateStaffWorkingScheduleSuccess:
      state.Staff?.updateStaffWorkingScheduleSuccess,
    loadingSearchStaffWorkingSchedule:
      state.Staff?.loadingSearchStaffWorkingSchedule,
    searchStaffWorkingScheduleSuccess:
      state.Staff?.searchStaffWorkingScheduleSuccess,
    staffListAPI: state.Staff?.staffList?.staff_list,
    loadingSearchStaffWorkingScheduleForClone:
      state.Staff?.loadingSearchStaffWorkingScheduleForClone,
    searchStaffWorkingScheduleForCloneSuccess:
      state.Staff?.searchStaffWorkingScheduleForCloneSuccess,
  }));

  const [scheduleId, setScheduleId] = useState(null);
  const [scheduleName, setScheduleName] = useState("Standard");
  // 'Fixed'  = availability equals working hours (current behaviour).
  // 'Flexible' = shift rows are an availability window; the diary auto-allocates
  //   within it up to each day's cap (see per-shift Daily cap / Minimum / rule).
  const [availabilityMode, setAvailabilityMode] = useState("Fixed");
  const [effectiveStartDate, setEffectiveStartDate] = useState(
    getTodayDateTimeStart()
  );
  const [days, setDays] = useState(buildDefaultDays);
  // "schedule" (weekly pattern) | "oneoff" (overtime / days off)
  const [activeSection, setActiveSection] = useState("schedule");
  // Change #100451 / #100452 - schedule-level limits. limitsReady = the loaded
  // schedule carries the columns (delta run), so they are only sent then.
  const [minimumRestHours, setMinimumRestHours] = useState("");
  const [homeArrivalGraceMins, setHomeArrivalGraceMins] = useState("");
  const [limitsReady, setLimitsReady] = useState(false);
  const [hasLoadedExistingSchedule, setHasLoadedExistingSchedule] =
    useState(false);
  const [isEditorVisible, setIsEditorVisible] = useState(false);

  // ── "Copy from another staff member" dialog state (isolated lookup) ──
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceStaffId, setCloneSourceStaffId] = useState(null);

  useEffect(() => {
    if (!staffId) return;

    // Reset local form state on every staff change so a previously-viewed
    // staff's schedule can never leak into a different staff member (e.g. a
    // newly created staff with no schedule of their own).
    setScheduleId(null);
    setMinimumRestHours("");
    setHomeArrivalGraceMins("");
    setLimitsReady(false);
    setScheduleName("Standard");
    setAvailabilityMode("Fixed");
    setEffectiveStartDate(getTodayDateTimeStart());
    setEffectiveEndDate("");
    setIsForever(true);
    setDays(buildDefaultDays());
    setHasLoadedExistingSchedule(false);
    setIsEditorVisible(false);

    dispatch(
      searchStaffWorkingSchedule([
        {
          staffworkingschedule_data: {
            Staff_Id: Number(staffId),
          },
        },
      ])
    );
  }, [dispatch, staffId]);

  // Staff list powers the "copy from another staff member" dropdown.
  useEffect(() => {
    dispatch(getStaffList());
  }, [dispatch]);

  useEffect(() => {
    if (loadingSearchStaffWorkingSchedule) return;
    if (!searchStaffWorkingScheduleSuccess) return;

    const list = getScheduleListFromSearchResponse(
      searchStaffWorkingScheduleSuccess
    );

    const newestSchedule = getNewestSchedule(list);

    if (!newestSchedule) {
      // No schedule for this staff — clear the whole form, not just the id, so
      // the editor doesn't fall back to a previously-loaded staff's days.
      setScheduleId(null);
      setMinimumRestHours("");
      setHomeArrivalGraceMins("");
      setLimitsReady(false);
      setScheduleName("Standard");
      setAvailabilityMode("Fixed");
      setEffectiveStartDate(getTodayDateTimeStart());
      setEffectiveEndDate("");
      setIsForever(true);
      setDays(buildDefaultDays());
      setHasLoadedExistingSchedule(true);
      return;
    }

    setScheduleId(Number(newestSchedule.Id));
    setLimitsReady(Object.prototype.hasOwnProperty.call(newestSchedule, "MinimumRestHours"));
    setMinimumRestHours(newestSchedule.MinimumRestHours ?? "");
    setHomeArrivalGraceMins(newestSchedule.HomeArrivalGraceMins ?? "");
    setScheduleName(newestSchedule.Name || "Standard");
    setAvailabilityMode(newestSchedule.AvailabilityMode || "Fixed");
    setEffectiveStartDate(
      newestSchedule.EffectiveStartDateTime || getTodayDateTimeStart()
    );

    const existingEndDate = newestSchedule.EffectiveEndDateTime;

    if (
      existingEndDate &&
      String(existingEndDate).toUpperCase() !== "NULL" &&
      String(existingEndDate) !== "0000-00-00 00:00:00"
    ) {
      setIsForever(false);
      setEffectiveEndDate(existingEndDate);
    } else {
      setIsForever(true);
      setEffectiveEndDate("");
    }
    setDays(
      scheduleRecordsToDays(
        newestSchedule.staffworkingschedulerecord_list || []
      )
    );
    setHasLoadedExistingSchedule(true);
  }, [loadingSearchStaffWorkingSchedule, searchStaffWorkingScheduleSuccess]);

  useEffect(() => {
    const res = createStaffWorkingScheduleSuccess;
    if (!res || !Object.keys(res).length) return;
    if (!isSavePendingRef.current) return;

    isSavePendingRef.current = false;
    const status = Array.isArray(res) ? res?.[0]?.status : res?.status;
    // Errors come back on the same channel with an explicit non-200 status
    // (e.g. { status: 400, message }). Treat an absent status as the legacy
    // success shape (the created record returned directly).
    const isSuccess = status === 200 || typeof status === "undefined";

    if (isSuccess) {
      const createdId = getScheduleIdFromResponse(res);
      if (createdId) setScheduleId(Number(createdId));
      toast.success(res?.message || "Schedule created successfully.");
      onSaveSuccess?.();
    } else {
      toast.error(res?.message || "Failed to create schedule.");
    }
  }, [createStaffWorkingScheduleSuccess]);

  useEffect(() => {
    const res = updateStaffWorkingScheduleSuccess;
    if (!res || !Object.keys(res).length) return;
    if (!isSavePendingRef.current) return;

    isSavePendingRef.current = false;
    const status = Array.isArray(res) ? res?.[0]?.status : res?.status;
    const isSuccess = status === 200 || typeof status === "undefined";

    if (isSuccess) {
      toast.success(res?.message || "Schedule updated successfully.");
      onSaveSuccess?.();
    } else {
      toast.error(res?.message || "Failed to update schedule.");
    }
  }, [updateStaffWorkingScheduleSuccess]);

  const isSaving =
    loadingCreateStaffWorkingSchedule || loadingUpdateStaffWorkingSchedule;

  const isInitialLoading =
    loadingSearchStaffWorkingSchedule && !hasLoadedExistingSchedule;

  const isUpdateMode = Boolean(scheduleId);
  const showEditor = isUpdateMode || isEditorVisible;

  const workingDaysCount = useMemo(
    () => Object.values(days).filter((day) => day.working).length,
    [days]
  );

  const totalShiftsCount = useMemo(
    () =>
      Object.values(days).reduce((total, day) => {
        if (!day.working) return total;
        return total + (day.shifts?.length || 0);
      }, 0),
    [days]
  );

  // Live running totals across all working days/shifts.
  const { workMinsExclBreaks, workMinsInclBreaks } = useMemo(() => {
    let incl = 0;
    let excl = 0;

    Object.values(days).forEach((day) => {
      if (!day.working) return;

      (day.shifts || []).forEach((shift) => {
        let duration =
          parseTimeToMinutes(shift.endTime) -
          parseTimeToMinutes(shift.startTime);
        if (duration < 0) duration += 24 * 60; // overnight shift wraps midnight

        incl += duration;
        excl += Math.max(0, duration - Number(shift.breakMins || 0));
      });
    });

    return { workMinsExclBreaks: excl, workMinsInclBreaks: incl };
  }, [days]);

  const toggleDayWorking = (dayKey, checked) => {
    setDays((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        working: checked,
        shifts:
          prev[dayKey]?.shifts?.length > 0
            ? prev[dayKey].shifts
            : [{ ...DEFAULT_SHIFT }],
      },
    }));
  };

  const updateShift = (dayKey, shiftIndex, patch) => {
    setDays((prev) => {
      const currentShifts = prev[dayKey]?.shifts || [{ ...DEFAULT_SHIFT }];

      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          shifts: currentShifts.map((shift, index) =>
            index === shiftIndex ? { ...shift, ...patch } : shift
          ),
        },
      };
    });
  };

  const addShift = (dayKey) => {
    setDays((prev) => {
      const currentShifts = prev[dayKey]?.shifts || [];

      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          working: true,
          shifts: [
            ...currentShifts,
            {
              id: null,
              startTime: "13:00",
              endTime: "17:00",
              breakMins: 0,
              notes: "Additional shift",
            },
          ],
        },
      };
    });
  };

  const removeShift = (dayKey, shiftIndex) => {
    setDays((prev) => {
      const currentShifts = prev[dayKey]?.shifts || [];
      const nextShifts = currentShifts.filter(
        (_, index) => index !== shiftIndex
      );

      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          working: nextShifts.length > 0,
          shifts: nextShifts.length > 0 ? nextShifts : [{ ...DEFAULT_SHIFT }],
        },
      };
    });
  };

  const [addressOpen, setAddressOpen] = useState({});

  // Task 24472 - the company's own premises, so a shift's start/end location
  // can be picked instead of retyped every time. Read directly rather than
  // through a store slice: it is a small read-only lookup, same as the asset
  // assignment panel does.
  const [companySites, setCompanySites] = useState([]);
  useEffect(() => {
    let cancelled = false;
    companySiteLocationListApi()
      .then((res) => {
        if (cancelled) return;
        setCompanySites(
          (res?.companysitelocation_list || []).filter(
            (x) => String(x.FlagActive) !== "0"
          )
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Change #100454 - the person's own addresses (Addresses tab, Task 24860)
  // alongside the company sites, so home can be picked rather than retyped.
  const [staffAddresses, setStaffAddresses] = useState([]);
  useEffect(() => {
    if (!staffId) return undefined;
    let cancelled = false;
    staffAddressListApi({ id: staffId })
      .then((res) => {
        if (cancelled) return;
        setStaffAddresses(res?.status === 200 ? res.staffaddress_list || [] : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [staffId]);

  const addressPatch = (src, isEnd) =>
    isEnd
      ? {
          endAddressLine1: src?.AddressLine1 || "",
          endAddressLine2: src?.AddressLine2 || "",
          endAddressLine3: src?.AddressLine3 || "",
          endAddressLine4: src?.AddressLine4 || "",
          endAddressCity: src?.AddressCity || "",
          endAddressCounty: src?.AddressCounty || "",
          endAddressPostCode: src?.AddressPostCode || "",
          endAddressCountry: src?.AddressCountry || "",
        }
      : {
          addressLine1: src?.AddressLine1 || "",
          addressLine2: src?.AddressLine2 || "",
          addressLine3: src?.AddressLine3 || "",
          addressLine4: src?.AddressLine4 || "",
          addressCity: src?.AddressCity || "",
          addressCounty: src?.AddressCounty || "",
          addressPostCode: src?.AddressPostCode || "",
          addressCountry: src?.AddressCountry || "",
        };

  // Change #100503 - the start / end location picker is ShiftLocationPicker
  // now (buttons for staff address, company site, customer or another
  // address). It copies the chosen address onto the shift through addressPatch.
  const toggleAddress = (dayKey, shiftIndex, suffix = "") => {
    const key = `${dayKey}-${shiftIndex}${suffix}`;
    setAddressOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // copyConfirm: { fromKey, fromLabel, toKey?, toLabel? } — toKey null = copy to all
  const [copyConfirm, setCopyConfirm] = useState(null);
  // copyMenuState: { anchor, fromKey, fromLabel } — which day's arrow was clicked
  const [copyMenuState, setCopyMenuState] = useState(null);
  // customDays: { fromKey, fromLabel, selectedKeys: [] } — multi-day clone picker
  const [customDays, setCustomDays] = useState(null);

  const copyDayToAll = (fromDayKey) => {
    const source = cloneDay(days[fromDayKey]);
    setDays((prev) => {
      const next = { ...prev };
      DAYS.forEach((day) => {
        if (day.key !== fromDayKey) next[day.key] = cloneDay(source);
      });
      return next;
    });
  };

  const copyDayTo = (fromDayKey, toDayKey) => {
    setDays((prev) => ({
      ...prev,
      [toDayKey]: cloneDay(prev[fromDayKey]),
    }));
  };

  const copyDayToKeys = (fromDayKey, targetKeys = []) => {
    const source = cloneDay(days[fromDayKey]);
    setDays((prev) => {
      const next = { ...prev };
      targetKeys.forEach((key) => {
        if (key !== fromDayKey) next[key] = cloneDay(source);
      });
      return next;
    });
  };

  const toggleCustomDay = (key) =>
    setCustomDays((prev) =>
      prev
        ? {
            ...prev,
            selectedKeys: prev.selectedKeys.includes(key)
              ? prev.selectedKeys.filter((k) => k !== key)
              : [...prev.selectedKeys, key],
          }
        : prev
    );

  const clearAll = () => {
    setDays(buildDefaultDays());
  };

  const buildRecordForShift = (shiftConfig, dayMeta) => {
    const record = {
      FlagActive: "1",
      FlagMon: dayMeta.key === "mon" ? "1" : "0",
      FlagTue: dayMeta.key === "tue" ? "1" : "0",
      FlagWed: dayMeta.key === "wed" ? "1" : "0",
      FlagThu: dayMeta.key === "thu" ? "1" : "0",
      FlagFri: dayMeta.key === "fri" ? "1" : "0",
      FlagSat: dayMeta.key === "sat" ? "1" : "0",
      FlagSun: dayMeta.key === "sun" ? "1" : "0",
      StartTime: toApiTime(shiftConfig.startTime),
      EndTime: toApiTime(shiftConfig.endTime),
      BreakTimeAllowanceMins: String(shiftConfig.breakMins || 0),
      // Flexible-availability fields. Cap/minimum only carry a value in Flexible
      // mode; in Fixed mode they are sent null so the diary ignores them.
      DailyCapMins:
        availabilityMode === "Flexible" &&
        shiftConfig.dailyCapMins !== null &&
        shiftConfig.dailyCapMins !== undefined &&
        shiftConfig.dailyCapMins !== ""
          ? Number(shiftConfig.dailyCapMins)
          : null,
      MinimumMins:
        availabilityMode === "Flexible" &&
        shiftConfig.minimumMins !== null &&
        shiftConfig.minimumMins !== undefined &&
        shiftConfig.minimumMins !== ""
          ? Number(shiftConfig.minimumMins)
          : null,
      CapRule: shiftConfig.capRule || "Span",
      Notes: shiftConfig.notes || "",
      AddressLine1: shiftConfig.addressLine1 || "",
      AddressLine2: shiftConfig.addressLine2 || "",
      AddressLine3: shiftConfig.addressLine3 || "",
      AddressLine4: shiftConfig.addressLine4 || "",
      AddressCity: shiftConfig.addressCity || "",
      AddressCounty: shiftConfig.addressCounty || "",
      AddressPostCode: shiftConfig.addressPostCode || "",
      AddressCountry: shiftConfig.addressCountry || "",
      EndAddressLine1: shiftConfig.endAddressLine1 || "",
      EndAddressLine2: shiftConfig.endAddressLine2 || "",
      EndAddressLine3: shiftConfig.endAddressLine3 || "",
      EndAddressLine4: shiftConfig.endAddressLine4 || "",
      EndAddressCity: shiftConfig.endAddressCity || "",
      EndAddressCounty: shiftConfig.endAddressCounty || "",
      EndAddressPostCode: shiftConfig.endAddressPostCode || "",
      EndAddressCountry: shiftConfig.endAddressCountry || "",
    };

    // NOTE: intentionally do NOT send a per-record `Id`. The loaded records
    // carry their own DB ids (e.g. the rogue 11), which would be re-sent on
    // every update and tie the save to a stale/foreign record. Records are
    // linked to the parent via StaffWorkingSchedule_Id only.
    if (scheduleId) {
      record.StaffWorkingSchedule_Id = Number(scheduleId);
    }

    return record;
  };

  const buildScheduleBody = () => {
    const staffworkingschedulerecord_list = DAYS.flatMap((day) => {
      const dayConfig = days[day.key];

      if (!dayConfig?.working) return [];

      return (dayConfig.shifts || []).map((shift) =>
        buildRecordForShift(shift, day)
      );
    });

    const body = {
      Staff_Id: Number(staffId),
      Name: scheduleName || "Standard",
      AvailabilityMode: availabilityMode || "Fixed",
      EffectiveStartDateTime: effectiveStartDate || getTodayDateTimeStart(),
      EffectiveEndDateTime: isForever ? "NULL" : effectiveEndDate,
      FlagActive: "1",
      staffworkingschedulerecord_list,
    };

    // Safe to always send: an older backend drops unknown keys and the new one
    // drops these until the delta has run.
    {
      body.MinimumRestHours = minimumRestHours === "" ? null : Number(minimumRestHours);
      body.HomeArrivalGraceMins = homeArrivalGraceMins === "" ? null : Number(homeArrivalGraceMins);
    }

    if (scheduleId) {
      body.Id = Number(scheduleId);
      body.StaffWorkingSchedule_Id = Number(scheduleId);
    }

    return body;
  };

  const buildPayload = () => [buildScheduleBody()];

  const validatePayload = (payload) => {
    if (!staffId) {
      console.error("Missing Staff_Id from route params.");
      return false;
    }

    if (!isForever && !effectiveEndDate) {
      console.error(
        "Please enter an effective end date, or turn Forever back on."
      );
      return false;
    }

    if (!payload[0].staffworkingschedulerecord_list.length) {
      console.error("Please select at least one working day.");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    const payload = buildPayload();

    if (!validatePayload(payload)) return;
    if (!isForever && !effectiveEndDate) {
      toast.error("Please select an end date.");
      return false;
    }

    const invalidDay = DAYS.find(
      (day) =>
        days[day.key]?.working &&
        (days[day.key]?.shifts || []).some(isShiftTimeInvalid)
    );
    if (invalidDay) {
      toast.error(`${invalidDay.label}: shift end time must be after start time.`);
      return;
    }

    isSavePendingRef.current = true;
    if (isUpdateMode) {
      dispatch(updateStaffWorkingSchedule(payload));
      return;
    }

    dispatch(createStaffWorkingSchedule(payload));
  };

  // ── Clone-from-another-staff helpers ──
  // Staff options for the dropdown, excluding the staff we're currently editing.
  const cloneStaffOptions = useMemo(
    () =>
      filterActiveStaff(staffListAPI || [])
        .filter((s) => Number(s.Id) !== Number(staffId))
        .map((s) => ({
          id: Number(s.Id),
          label: s.FullName || `Staff #${s.Id}`,
        })),
    [staffListAPI, staffId]
  );

  // Newest schedule found for the selected source staff (isolated channel).
  const clonePreviewSchedule = useMemo(() => {
    if (!cloneSourceStaffId) return null;
    const list = getScheduleListFromSearchResponse(
      searchStaffWorkingScheduleForCloneSuccess
    );
    return getNewestSchedule(list);
  }, [cloneSourceStaffId, searchStaffWorkingScheduleForCloneSuccess]);

  const clonePreviewDays = useMemo(
    () =>
      clonePreviewSchedule
        ? scheduleRecordsToDays(
            clonePreviewSchedule.staffworkingschedulerecord_list || []
          )
        : null,
    [clonePreviewSchedule]
  );

  const openCloneDialog = () => setCloneDialogOpen(true);

  const closeCloneDialog = () => {
    setCloneDialogOpen(false);
    setCloneSourceStaffId(null);
  };

  const handleSelectCloneStaff = (option) => {
    const id = option?.id ?? null;
    setCloneSourceStaffId(id);
    if (!id) return;
    dispatch(
      searchStaffWorkingScheduleForClone([
        {
          staffworkingschedule_data: {
            Staff_Id: Number(id),
          },
        },
      ])
    );
  };

  const handleApplyClone = () => {
    if (!clonePreviewSchedule) return;

    // Strip the source record ids so saving creates fresh records for the
    // current staff (and keep the current staff's own scheduleId untouched, so
    // a save updates their existing schedule rather than the source's).
    const parsed = scheduleRecordsToDays(
      clonePreviewSchedule.staffworkingschedulerecord_list || []
    );
    Object.keys(parsed).forEach((key) => {
      parsed[key] = {
        ...parsed[key],
        shifts: parsed[key].shifts.map((shift) => ({ ...shift, id: null })),
      };
    });

    setDays(parsed);
    setScheduleName(clonePreviewSchedule.Name || "Standard");
    setAvailabilityMode(clonePreviewSchedule.AvailabilityMode || "Fixed");
    setEffectiveStartDate(
      clonePreviewSchedule.EffectiveStartDateTime || getTodayDateTimeStart()
    );

    const endDate = clonePreviewSchedule.EffectiveEndDateTime;
    if (
      endDate &&
      String(endDate).toUpperCase() !== "NULL" &&
      String(endDate) !== "0000-00-00 00:00:00"
    ) {
      setIsForever(false);
      setEffectiveEndDate(endDate);
    } else {
      setIsForever(true);
      setEffectiveEndDate("");
    }

    setIsEditorVisible(true);
    closeCloneDialog();
    toast.success(
      "Schedule cloned into the editor. Review and save to apply it to this staff member."
    );
  };

  // Follow-up to Change #100450 - "style this into two tabs, schedules and one
  // off/overtime" and "this screen UX is very different to the rest". Now the
  // same InfoCard header + segmented pill tabs as the other staff tabs.
  const scheduleStats = [
    {
      icon: "ri-calendar-check-line",
      label: "Working days",
      value: workingDaysCount,
    },
    { icon: "ri-time-line", label: "Shifts", value: totalShiftsCount },
    {
      icon: "ri-timer-line",
      label: availabilityMode === "Flexible" ? "Window (excl. breaks)" : "Hours (excl. breaks)",
      value: formatMinutes(workMinsExclBreaks),
    },
    {
      icon: "ri-cup-line",
      label: availabilityMode === "Flexible" ? "Window (incl. breaks)" : "Hours (incl. breaks)",
      value: formatMinutes(workMinsInclBreaks),
    },
  ];

  const headerActions = (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {isInitialLoading ? (
        <span className="badge bg-light text-muted">
          <CircularProgress size={10} sx={{ mr: 0.5 }} /> Loading
        </span>
      ) : isUpdateMode ? (
        <span className="badge bg-light text-success">
          {scheduleName || "Standard"} ({availabilityMode})
        </span>
      ) : (
        <span className="badge bg-light text-warning">New schedule</span>
      )}
      {activeSection === "schedule" && (
        <>
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={openCloneDialog}
            disabled={isInitialLoading}
          >
            <i className="ri-group-line me-1"></i>Copy from staff
          </button>
          {showEditor && (
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={clearAll}
              disabled={isInitialLoading}
              title="Turn every day off so you can start again (nothing is saved until you press Save)"
            >
              <i className="ri-eraser-line me-1"></i>Clear days
            </button>
          )}
          {showEditor && (
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={handleSubmit}
              disabled={isSaving || isInitialLoading || !workingDaysCount}
            >
              <i className="ri-save-3-line me-1"></i>
              {isSaving ? "Saving..." : isUpdateMode ? "Save schedule" : "Create schedule"}
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <Box sx={{ p: { xs: 0, md: 0 } }}>
      <InfoCard
        title="Working Schedule"
        icon="ri-calendar-schedule-line"
        accent="indigo"
        actions={headerActions}
      >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <div className="single-shell-tabs-wrap mb-0">
              <div className="single-shell-tabs nav" role="tablist">
                <button
                  type="button"
                  className={`nav-link border-0 ${activeSection === "schedule" ? "active" : ""}`}
                  onClick={() => setActiveSection("schedule")}
                >
                  <i className="ri-calendar-2-line"></i>
                  <span>Weekly schedule</span>
                </button>
                <Tooltip title={scheduleId ? "" : "Save a weekly schedule first"}>
                  <span>
                    <button
                      type="button"
                      className={`nav-link border-0 ${activeSection === "oneoff" ? "active" : ""}`}
                      onClick={() => scheduleId && setActiveSection("oneoff")}
                      disabled={!scheduleId}
                      style={!scheduleId ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    >
                      <i className="ri-time-line"></i>
                      <span>One-off &amp; overtime</span>
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
            <Typography variant="body2" color="text.secondary">
              {activeSection === "schedule"
                ? "The normal weekly pattern. Add one or more shifts per day."
                : "Extra time and days off on a single date. The diary and dispatch book into extra time."}
            </Typography>
          </Stack>

          {activeSection === "oneoff" && (
            <ScheduleAdjustments
              staffId={staffId}
              scheduleId={scheduleId}
              minimumRestHours={minimumRestHours}
              embedded
            />
          )}

          {activeSection === "schedule" && showEditor && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {scheduleStats.map((s) => (
                <Grid item xs={6} md={3} key={s.label}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.25,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(79, 70, 229, 0.10)",
                        color: "#4f46e5",
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    >
                      <i className={s.icon}></i>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {s.label}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>{s.value}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {activeSection === "schedule" && (
          <>

          {/* ── Empty state: no schedule found, editor not yet opened ── */}
          {hasLoadedExistingSchedule && !isInitialLoading && !showEditor && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarMonth sx={{ fontSize: 36, color: "primary.main" }} />
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  No working schedule yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 380 }}
                >
                  Define which days this staff member works and their shift
                  times. Once saved, it becomes their recurring weekly
                  availability.
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<CalendarMonth />}
                onClick={() => setIsEditorVisible(true)}
              >
                Set Up Working Schedule
              </Button>
            </Box>
          )}

          {/* ── Schedule editor ── */}
          {showEditor && (
            <>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
            <Grid item xs={12} md={3}>
              <SettingCard label="Schedule Name">
                <TextField
                  fullWidth
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  size="small"
                  disabled={isInitialLoading}
                  placeholder="Standard"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.paper",
                    },
                  }}
                />
              </SettingCard>
            </Grid>

            <Grid item xs={12} md={3}>
              <SettingCard
                label="Availability Mode"
                tone={availabilityMode === "Flexible" ? "amber" : "default"}
                helper={
                  availabilityMode === "Flexible"
                    ? "Shift times are an availability window; the diary books within it up to each day's cap."
                    : "Availability equals the working hours below."
                }
              >
                <ButtonGroup
                  fullWidth
                  size="small"
                  disabled={isInitialLoading}
                >
                  <Button
                    variant={
                      availabilityMode === "Fixed" ? "contained" : "outlined"
                    }
                    onClick={() => setAvailabilityMode("Fixed")}
                  >
                    Fixed
                  </Button>
                  <Button
                    variant={
                      availabilityMode === "Flexible" ? "contained" : "outlined"
                    }
                    onClick={() => setAvailabilityMode("Flexible")}
                  >
                    Flexible
                  </Button>
                </ButtonGroup>
              </SettingCard>
            </Grid>

            <Grid item xs={12} md={3}>
              <SettingCard label="Effective Start">
                {/* Change #100164 - a date picker rather than a free-text
                    "YYYY-MM-DD HH:mm:ss" box. A schedule starts on a day, so
                    the time is always midnight. */}
                <TextField
                  fullWidth
                  type="date"
                  value={toDateInput(effectiveStartDate)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEffectiveStartDate(value ? `${value} 00:00:00` : "");
                    // Keep the end on or after the start.
                    if (
                      value &&
                      effectiveEndDate &&
                      toDateInput(effectiveEndDate) < value
                    ) {
                      setEffectiveEndDate(`${value} 23:59:59`);
                    }
                  }}
                  size="small"
                  disabled={isInitialLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.paper",
                    },
                  }}
                />
              </SettingCard>
            </Grid>

            <Grid item xs={12} md={3}>
              <SettingCard
                label="Schedule End"
                title={isForever ? "Runs forever" : "Ends on date"}
                tone={isForever ? "blue" : "amber"}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Forever
                    </Typography>

                    <Switch
                      size="small"
                      checked={isForever}
                      disabled={isInitialLoading}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsForever(checked);

                        if (checked) {
                          setEffectiveEndDate("");
                        } else if (!effectiveEndDate) {
                          // Open the picker on the start date rather than an
                          // empty box, so there is no scrolling back to it.
                          const start =
                            toDateInput(effectiveStartDate) ||
                            toDateInput(getTodayDateTimeStart());
                          setEffectiveEndDate(`${start} 23:59:59`);
                        }
                      }}
                    />
                  </Stack>

                  <Collapse in={!isForever} timeout={180} unmountOnExit>
                    <TextField
                      fullWidth
                      type="date"
                      value={toDateInput(effectiveEndDate)}
                      onChange={(e) =>
                        setEffectiveEndDate(
                          e.target.value ? `${e.target.value} 23:59:59` : ""
                        )
                      }
                      inputProps={{
                        min: toDateInput(effectiveStartDate) || undefined,
                      }}
                      size="small"
                      disabled={isInitialLoading}
                      helperText="Last day the schedule applies"
                      sx={{
                        mt: 0.5,
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "background.paper",
                        },
                      }}
                    />
                  </Collapse>

                </Stack>
              </SettingCard>
            </Grid>

            {(limitsReady || !scheduleId) && (
              <>
                {/* Change #100451 */}
                <Grid item xs={12} md={3}>
                  <SettingCard
                    label="Minimum Rest"
                    helper="Hours off needed between one working day and the next. Overtime that breaks it is refused. Blank = no rule."
                  >
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="e.g. 11"
                      value={minimumRestHours}
                      onChange={(e) => setMinimumRestHours(e.target.value)}
                      inputProps={{ min: 0, step: 0.5 }}
                      InputProps={{ endAdornment: <Typography variant="caption">hrs</Typography> }}
                      disabled={isInitialLoading}
                      sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
                    />
                  </SettingCard>
                </Grid>
                {/* Change #100452 */}
                <Grid item xs={12} md={3}>
                  <SettingCard
                    label="Home By"
                    helper="Must be back at the end location (or start, if none) within this many minutes of the shift end. Dispatch will not offer a job that breaks it. Blank = no rule."
                  >
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="e.g. 60"
                      value={homeArrivalGraceMins}
                      onChange={(e) => setHomeArrivalGraceMins(e.target.value)}
                      inputProps={{ min: 0, step: 5 }}
                      InputProps={{ endAdornment: <Typography variant="caption">mins</Typography> }}
                      disabled={isInitialLoading}
                      sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
                    />
                  </SettingCard>
                </Grid>
              </>
            )}

          </Grid>
          <Stack spacing={1.5}>
            {DAYS.map((day) => {
              const dayValue = days[day.key] || DEFAULT_DAY;
              const shifts = dayValue.shifts || [];

              return (
                <Paper
                  key={day.key}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: "4px solid",
                    borderLeftColor: dayValue.working ? "#4f46e5" : "divider",
                    bgcolor: "background.paper",
                    opacity: dayValue.working ? 1 : 0.85,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch
                            checked={dayValue.working}
                            disabled={isInitialLoading}
                            onChange={(e) =>
                              toggleDayWorking(day.key, e.target.checked)
                            }
                          />

                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>
                              {day.label}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {dayValue.working
                                ? `${shifts.length} shift${
                                    shifts.length === 1 ? "" : "s"
                                  }`
                                : "Not working"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                          {dayValue.working && (
                            <ButtonGroup
                              size="small"
                              variant="outlined"
                              color="secondary"
                              disabled={isInitialLoading}
                            >
                              {/* Left — copy to ALL other days */}
                              <Button
                                startIcon={<ContentCopy />}
                                onClick={() =>
                                  setCopyConfirm({
                                    fromKey: day.key,
                                    fromLabel: day.label,
                                    toKey: null,
                                    toLabel: null,
                                  })
                                }
                              >
                                Copy to all
                              </Button>

                              {/* Right arrow — pick a specific day */}
                              <Button
                                size="small"
                                sx={{ px: 0.5 }}
                                onClick={(e) =>
                                  setCopyMenuState({
                                    anchor: e.currentTarget,
                                    fromKey: day.key,
                                    fromLabel: day.label,
                                  })
                                }
                              >
                                <ArrowDropDown />
                              </Button>
                            </ButtonGroup>
                          )}

                          <Button
                            size="small"
                            variant="outlined"
                            className="me-2"
                            startIcon={<Add />}
                            onClick={() => addShift(day.key)}
                            disabled={isInitialLoading}
                          >
                            Add shift
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>

                    {dayValue.working &&
                      shifts.map((shift, shiftIndex) => (
                        <Paper
                          key={`${day.key}-${shiftIndex}`}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: "rgba(79, 70, 229, 0.03)",
                            borderColor: "rgba(79, 70, 229, 0.15)",
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={1.2}>
                              <Chip
                                size="small"
                                label={`Shift ${shiftIndex + 1}`}
                                sx={{ bgcolor: "rgba(79, 70, 229, 0.10)", color: "#4f46e5", fontWeight: 700 }}
                              />
                            </Grid>

                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                type="time"
                                label={
                                  availabilityMode === "Flexible"
                                    ? "Window start"
                                    : "Start"
                                }
                                value={shift.startTime}
                                onChange={(e) =>
                                  updateShift(day.key, shiftIndex, {
                                    startTime: e.target.value,
                                  })
                                }
                                size="small"
                                disabled={isInitialLoading}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>

                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                type="time"
                                label={
                                  availabilityMode === "Flexible"
                                    ? "Window end"
                                    : "End"
                                }
                                value={shift.endTime}
                                onChange={(e) =>
                                  updateShift(day.key, shiftIndex, {
                                    endTime: e.target.value,
                                  })
                                }
                                size="small"
                                disabled={isInitialLoading}
                                InputLabelProps={{ shrink: true }}
                                error={isShiftTimeInvalid(shift)}
                                helperText={
                                  isShiftTimeInvalid(shift)
                                    ? "End must be after start"
                                    : undefined
                                }
                              />
                            </Grid>

                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Break mins"
                                value={shift.breakMins}
                                onChange={(e) =>
                                  updateShift(day.key, shiftIndex, {
                                    breakMins: Number(e.target.value || 0),
                                  })
                                }
                                size="small"
                                disabled={isInitialLoading}
                                inputProps={{ min: 0, step: 5 }}
                              />
                            </Grid>

                            <Grid item xs={12} md={3.8}>
                              <TextField
                                fullWidth
                                label="Notes"
                                value={shift.notes}
                                onChange={(e) =>
                                  updateShift(day.key, shiftIndex, {
                                    notes: e.target.value,
                                  })
                                }
                                size="small"
                                disabled={isInitialLoading}
                                placeholder="Morning shift, afternoon shift..."
                              />
                            </Grid>

                            <Grid item xs={12} md={1}>
                              <Tooltip title="Remove shift">
                                <span>
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      removeShift(day.key, shiftIndex)
                                    }
                                    disabled={
                                      isInitialLoading || shifts.length <= 1
                                    }
                                  >
                                    <DeleteOutline />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Grid>

                            {/* Flexible-availability controls */}
                            {availabilityMode === "Flexible" && (
                              <Grid item xs={12}>
                                <Grid
                                  container
                                  spacing={2}
                                  alignItems="flex-start"
                                >
                                  <Grid item xs={6} md={3}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      label="Daily cap (hours)"
                                      value={
                                        shift.dailyCapMins === null ||
                                        shift.dailyCapMins === undefined
                                          ? ""
                                          : shift.dailyCapMins / 60
                                      }
                                      onChange={(e) =>
                                        updateShift(day.key, shiftIndex, {
                                          dailyCapMins:
                                            e.target.value === ""
                                              ? null
                                              : Math.round(
                                                  Number(e.target.value) * 60
                                                ),
                                        })
                                      }
                                      size="small"
                                      disabled={isInitialLoading}
                                      inputProps={{ min: 0, step: 0.25 }}
                                      helperText="Max worked day inside the window"
                                    />
                                  </Grid>

                                  <Grid item xs={6} md={3}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      label="Minimum (hours)"
                                      value={
                                        shift.minimumMins === null ||
                                        shift.minimumMins === undefined
                                          ? ""
                                          : shift.minimumMins / 60
                                      }
                                      onChange={(e) =>
                                        updateShift(day.key, shiftIndex, {
                                          minimumMins:
                                            e.target.value === ""
                                              ? null
                                              : Math.round(
                                                  Number(e.target.value) * 60
                                                ),
                                        })
                                      }
                                      size="small"
                                      disabled={isInitialLoading}
                                      inputProps={{ min: 0, step: 0.25 }}
                                      helperText="Recorded minimum for the day"
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={5}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.25 }}
                                    >
                                      Cap rule
                                    </Typography>
                                    <ButtonGroup
                                      size="small"
                                      disabled={isInitialLoading}
                                    >
                                      <Button
                                        variant={
                                          (shift.capRule || "Span") === "Span"
                                            ? "contained"
                                            : "outlined"
                                        }
                                        onClick={() =>
                                          updateShift(day.key, shiftIndex, {
                                            capRule: "Span",
                                          })
                                        }
                                      >
                                        First to last
                                      </Button>
                                      <Button
                                        variant={
                                          shift.capRule === "Worked"
                                            ? "contained"
                                            : "outlined"
                                        }
                                        onClick={() =>
                                          updateShift(day.key, shiftIndex, {
                                            capRule: "Worked",
                                          })
                                        }
                                      >
                                        Worked only
                                      </Button>
                                    </ButtonGroup>
                                  </Grid>
                                </Grid>
                              </Grid>
                            )}

                            {/* Start location toggle */}
                            <Grid item xs={12} sm={6}>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<LocationOn sx={{ fontSize: 14 }} />}
                                endIcon={
                                  addressOpen[`${day.key}-${shiftIndex}`]
                                    ? <ExpandLess sx={{ fontSize: 14 }} />
                                    : <ExpandMore sx={{ fontSize: 14 }} />
                                }
                                onClick={() => toggleAddress(day.key, shiftIndex)}
                                disabled={isInitialLoading}
                                sx={{ fontSize: 11, color: "text.secondary", px: 0 }}
                              >
                                {shift.addressLine1 || shift.addressCity
                                  ? `Start: ${[shift.addressLine1, shift.addressCity].filter(Boolean).join(", ")}`
                                  : "Start location"}
                              </Button>
                            </Grid>

                            {/* End location toggle */}
                            <Grid item xs={12} sm={6}>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<LocationOn sx={{ fontSize: 14 }} />}
                                endIcon={
                                  addressOpen[`${day.key}-${shiftIndex}-end`]
                                    ? <ExpandLess sx={{ fontSize: 14 }} />
                                    : <ExpandMore sx={{ fontSize: 14 }} />
                                }
                                onClick={() => toggleAddress(day.key, shiftIndex, "-end")}
                                disabled={isInitialLoading}
                                sx={{ fontSize: 11, color: "text.secondary", px: 0 }}
                              >
                                {shift.endAddressLine1 || shift.endAddressCity
                                  ? `End: ${[shift.endAddressLine1, shift.endAddressCity].filter(Boolean).join(", ")}`
                                  : "End location"}
                              </Button>
                            </Grid>

                            {/* Address fields */}
                            <Grid item xs={12}>
                              <Collapse in={!!addressOpen[`${day.key}-${shiftIndex}`]} timeout={180} unmountOnExit>
                                <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
                                  <Grid item xs={12}>
                                  <ShiftLocationPicker
                                    address={{
                                      AddressLine1: shift.addressLine1,
                                      AddressLine2: shift.addressLine2,
                                      AddressCity: shift.addressCity,
                                      AddressPostCode: shift.addressPostCode,
                                    }}
                                    onPick={(src) => updateShift(day.key, shiftIndex, addressPatch(src, false))}
                                    companySites={companySites}
                                    staffAddresses={staffAddresses}
                                    staffId={staffId}
                                    disabled={isInitialLoading}
                                    renderFields={() => (
                                  <Grid container spacing={1.5}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 1"
                                      value={shift.addressLine1}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressLine1: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 2"
                                      value={shift.addressLine2}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressLine2: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 3"
                                      value={shift.addressLine3}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressLine3: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 4"
                                      value={shift.addressLine4}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressLine4: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="City"
                                      value={shift.addressCity}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressCity: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="County"
                                      value={shift.addressCounty}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressCounty: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Post Code"
                                      value={shift.addressPostCode}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressPostCode: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Country"
                                      value={shift.addressCountry}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { addressCountry: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  </Grid>
                                    )}
                                  />
                                  </Grid>
                                </Grid>
                              </Collapse>
                            </Grid>

                            {/* End location fields */}
                            <Grid item xs={12}>
                              <Collapse in={!!addressOpen[`${day.key}-${shiftIndex}-end`]} timeout={180} unmountOnExit>
                                <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                      Where the shift ends (e.g. home). Used by the diary as the drive-home destination for the last job.
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                  <ShiftLocationPicker
                                    isEnd
                                    address={{
                                      AddressLine1: shift.endAddressLine1,
                                      AddressLine2: shift.endAddressLine2,
                                      AddressCity: shift.endAddressCity,
                                      AddressPostCode: shift.endAddressPostCode,
                                    }}
                                    onPick={(src) => updateShift(day.key, shiftIndex, addressPatch(src, true))}
                                    companySites={companySites}
                                    staffAddresses={staffAddresses}
                                    staffId={staffId}
                                    disabled={isInitialLoading}
                                    renderFields={() => (
                                  <Grid container spacing={1.5}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 1"
                                      value={shift.endAddressLine1}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressLine1: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 2"
                                      value={shift.endAddressLine2}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressLine2: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 3"
                                      value={shift.endAddressLine3}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressLine3: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Address Line 4"
                                      value={shift.endAddressLine4}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressLine4: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="City"
                                      value={shift.endAddressCity}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressCity: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="County"
                                      value={shift.endAddressCounty}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressCounty: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Post Code"
                                      value={shift.endAddressPostCode}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressPostCode: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Country"
                                      value={shift.endAddressCountry}
                                      onChange={(e) => updateShift(day.key, shiftIndex, { endAddressCountry: e.target.value })}
                                      disabled={isInitialLoading}
                                    />
                                  </Grid>
                                  </Grid>
                                    )}
                                  />
                                  </Grid>
                                </Grid>
                              </Collapse>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

            </>
          )} {/* end showEditor */}
          </>
          )} {/* end schedule section */}
      </InfoCard>

      {/* ── Day-picker dropdown menu (arrow button) ── */}
      <Menu
        anchorEl={copyMenuState?.anchor}
        open={Boolean(copyMenuState)}
        onClose={() => setCopyMenuState(null)}
      >
        <MenuItem disabled sx={{ fontSize: 12, opacity: 0.6 }}>
          Copy <strong style={{ margin: "0 4px" }}>{copyMenuState?.fromLabel}</strong> to…
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCopyMenuState(null);
            setCopyConfirm({
              fromKey: copyMenuState.fromKey,
              fromLabel: copyMenuState.fromLabel,
              toKeys: WEEKDAY_KEYS,
              groupLabel: "All Weekdays (Mon–Fri)",
            });
          }}
        >
          All Weekdays (Mon–Fri)
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCopyMenuState(null);
            setCopyConfirm({
              fromKey: copyMenuState.fromKey,
              fromLabel: copyMenuState.fromLabel,
              toKeys: WEEKEND_KEYS,
              groupLabel: "Weekend (Sat–Sun)",
            });
          }}
        >
          Weekend (Sat–Sun)
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCopyMenuState(null);
            setCopyConfirm({
              fromKey: copyMenuState.fromKey,
              fromLabel: copyMenuState.fromLabel,
              toKeys: EVERYDAY_KEYS,
              groupLabel: "Everyday",
            });
          }}
        >
          Everyday
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCustomDays({
              fromKey: copyMenuState.fromKey,
              fromLabel: copyMenuState.fromLabel,
              selectedKeys: [],
            });
            setCopyMenuState(null);
          }}
        >
          Custom days…
        </MenuItem>
        <Divider />
        {DAYS.filter((d) => d.key !== copyMenuState?.fromKey).map((d) => (
          <MenuItem
            key={d.key}
            onClick={() => {
              setCopyMenuState(null);
              setCopyConfirm({
                fromKey: copyMenuState.fromKey,
                fromLabel: copyMenuState.fromLabel,
                toKey: d.key,
                toLabel: d.label,
              });
            }}
          >
            {d.label}
          </MenuItem>
        ))}
      </Menu>

      {/* ── Copy confirmation dialog ── */}
      <Dialog
        open={Boolean(copyConfirm)}
        onClose={() => setCopyConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {copyConfirm?.toKey
            ? `Copy ${copyConfirm.fromLabel} → ${copyConfirm.toLabel}?`
            : copyConfirm?.toKeys
            ? `Copy ${copyConfirm.fromLabel} to ${copyConfirm.groupLabel}?`
            : `Copy ${copyConfirm?.fromLabel} to all other days?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {copyConfirm?.toKey ? (
              <>
                This will overwrite <strong>{copyConfirm.toLabel}</strong>'s
                shifts with <strong>{copyConfirm.fromLabel}</strong>'s schedule
                and mark it as working.
              </>
            ) : copyConfirm?.toKeys ? (
              <>
                This will overwrite{" "}
                <strong>{copyConfirm.groupLabel}</strong> with{" "}
                <strong>{copyConfirm.fromLabel}</strong>'s schedule and mark
                those days as working.
              </>
            ) : (
              <>
                This will overwrite every other day's shifts with{" "}
                <strong>{copyConfirm?.fromLabel}</strong>'s schedule and mark
                them all as working.
              </>
            )}{" "}
            Save the schedule afterwards to persist the changes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={() => {
              if (copyConfirm.toKey) {
                copyDayTo(copyConfirm.fromKey, copyConfirm.toKey);
              } else if (copyConfirm.toKeys) {
                copyDayToKeys(copyConfirm.fromKey, copyConfirm.toKeys);
              } else {
                copyDayToAll(copyConfirm.fromKey);
              }
              setCopyConfirm(null);
            }}
          >
            {copyConfirm?.toKey || copyConfirm?.toKeys
              ? "Yes, copy"
              : "Yes, copy to all"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Custom days picker dialog (clone source day into multiple days) ── */}
      <Dialog
        open={Boolean(customDays)}
        onClose={() => setCustomDays(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Copy {customDays?.fromLabel} to…</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 1 }}>
            Tick the days to overwrite with{" "}
            <strong>{customDays?.fromLabel}</strong>'s shifts. They&apos;ll be
            marked as working. Save afterwards to persist.
          </DialogContentText>

          {(() => {
            const otherDays = DAYS.filter(
              (d) => d.key !== customDays?.fromKey
            );
            const selectedCount = customDays?.selectedKeys?.length || 0;
            const allSelected =
              otherDays.length > 0 && selectedCount === otherDays.length;

            return (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedCount > 0 && !allSelected}
                      onChange={(e) =>
                        setCustomDays((prev) =>
                          prev
                            ? {
                                ...prev,
                                selectedKeys: e.target.checked
                                  ? otherDays.map((d) => d.key)
                                  : [],
                              }
                            : prev
                        )
                      }
                    />
                  }
                  label={<em>Select all</em>}
                />
                <Divider sx={{ my: 0.5 }} />
                {otherDays.map((d) => (
                  <FormControlLabel
                    key={d.key}
                    control={
                      <Checkbox
                        checked={
                          customDays?.selectedKeys?.includes(d.key) || false
                        }
                        onChange={() => toggleCustomDay(d.key)}
                      />
                    }
                    label={d.label}
                  />
                ))}
              </FormGroup>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDays(null)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            disabled={!customDays?.selectedKeys?.length}
            onClick={() => {
              copyDayToKeys(customDays.fromKey, customDays.selectedKeys);
              setCustomDays(null);
            }}
          >
            Copy to {customDays?.selectedKeys?.length || 0} day
            {customDays?.selectedKeys?.length === 1 ? "" : "s"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Copy from another staff member dialog (isolated lookup) ── */}
      <Dialog
        open={cloneDialogOpen}
        onClose={closeCloneDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy schedule from another staff member</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Pick a staff member to preview their current working schedule, then
              clone it into this staff member&apos;s editor. Nothing is saved
              until you review and click save.
            </DialogContentText>

            <Autocomplete
              options={cloneStaffOptions}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              value={
                cloneStaffOptions.find((o) => o.id === cloneSourceStaffId) ||
                null
              }
              onChange={(_, val) => handleSelectCloneStaff(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Staff member"
                  placeholder="Select staff..."
                />
              )}
            />

            {/* Preview area */}
            {cloneSourceStaffId && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                {loadingSearchStaffWorkingScheduleForClone ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 2 }}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading schedule...
                    </Typography>
                  </Stack>
                ) : !clonePreviewSchedule ? (
                  <Typography variant="body2" color="text.secondary">
                    This staff member has no active working schedule to copy.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography sx={{ fontWeight: 700 }}>
                        {clonePreviewSchedule.Name || "Standard"}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`From ${
                          clonePreviewSchedule.EffectiveStartDateTime || "—"
                        }`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          clonePreviewSchedule.EffectiveEndDateTime &&
                          String(
                            clonePreviewSchedule.EffectiveEndDateTime
                          ).toUpperCase() !== "NULL"
                            ? `To ${clonePreviewSchedule.EffectiveEndDateTime}`
                            : "Runs forever"
                        }
                      />
                    </Stack>

                    <Divider />

                    {DAYS.map((day) => {
                      const dayValue = clonePreviewDays?.[day.key];
                      const working = dayValue?.working;
                      return (
                        <Stack
                          key={day.key}
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, minWidth: 96 }}
                          >
                            {day.label}
                          </Typography>
                          {working ? (
                            <Stack spacing={0.25}>
                              {dayValue.shifts.map((shift, i) => (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {shift.startTime} – {shift.endTime}
                                  {shift.breakMins
                                    ? ` (${shift.breakMins}m break)`
                                    : ""}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              Not working
                            </Typography>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCloneDialog} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={handleApplyClone}
            disabled={
              !clonePreviewSchedule ||
              loadingSearchStaffWorkingScheduleForClone
            }
          >
            Clone this schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffWorkingSchedule;
